import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { Toolbar, PaletteFooter } from "@/pages/MoodboardA";

// Route C · "Speelruimte"
// Palette: lichtblauw #97B79D, geel #F0B98D, koraal #E06B3F accent, off-white #FAF7F1
// Motif: kronkels/squiggles — handgetekende lijnen
// Photos: skateboarders, klimmers, lachende groepen, mixed collage

const PALETTE = {
  bg: "#FAF7F1",
  ink: "#1F5132",
  lichtblauw: "#97B79D",
  geel: "#F0B98D",
  koraal: "#E06B3F",
  surface: "#EEF3EC",
};

const IMGS = {
  skate: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  lachen: "https://images.unsplash.com/photo-1543269664-56d93c1b41a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  klimmen: "https://images.unsplash.com/photo-1522163182402-834f871fd851?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  spelen: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  jumpen: "https://images.unsplash.com/photo-1502680390469-be75c86b636f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

const ease = [0.22, 1, 0.36, 1];

function Squiggle({ color, className = "", d, delay = 0 }) {
  return (
    <svg className={className} viewBox="0 0 400 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <motion.path
        d={d || "M 5 50 Q 40 5, 80 50 T 160 50 T 240 50 T 320 50 T 395 50"}
        stroke={color}
        strokeWidth="5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.8, delay, ease }}
      />
    </svg>
  );
}

function HeroDeelnemer() {
  return (
    <section className="relative overflow-hidden" style={{ background: PALETTE.bg, color: PALETTE.ink }}>
      {/* Floating shapes */}
      <div className="absolute top-32 right-10 w-32 h-32 rounded-full opacity-90 hidden md:block" style={{ background: PALETTE.geel }} aria-hidden />
      <div className="absolute bottom-40 left-8 w-24 h-24 rounded-full opacity-80 hidden md:block" style={{ background: PALETTE.koraal }} aria-hidden />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-20 pb-24 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative">
            <p className="text-xs uppercase tracking-[0.35em]" style={{ color: PALETTE.koraal }}>
              Ay yo · Voor jou
            </p>
            <h1 className="font-heading font-light text-[46px] sm:text-6xl lg:text-[92px] leading-[0.96] tracking-tight mt-8">
              Speelruimte
              <br />
              <span className="italic relative inline-block">
                om te ontdekken
                <Squiggle color={PALETTE.koraal} className="absolute left-0 -bottom-3 w-full h-4" d="M 5 20 Q 60 -5, 120 20 T 240 20 T 395 20" />
              </span>
              <br />
              wat wél werkt.
            </h1>
            <p className="mt-8 max-w-lg text-lg leading-relaxed" style={{ color: PALETTE.ink + "c0" }}>
              Geen plan bij binnenkomst. Wél tijd, ruimte en mensen om samen uit te zoeken waar jouw vonk zit.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {[
                { w: "Startpunt", c: PALETTE.geel },
                { w: "Rugzak", c: PALETTE.lichtblauw },
                { w: "Rem", c: PALETTE.koraal },
                { w: "Vonk", c: PALETTE.geel },
                { w: "LevelMoment", c: PALETTE.lichtblauw },
              ].map((w) => (
                <span key={w.w} className="text-sm px-4 py-2 rounded-full font-medium" style={{ background: w.c, color: PALETTE.ink }}>
                  {w.w}
                </span>
              ))}
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start">
              <button className="group inline-flex items-center gap-4 rounded-full pl-7 pr-2 py-2 transition-all" style={{ background: PALETTE.ink, color: PALETTE.bg }}>
                <span className="text-base font-medium flex items-center gap-2"><Sparkles size={16} /> Vind jouw vonk</span>
                <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: PALETTE.geel }}>
                  <ArrowUpRight size={18} color={PALETTE.ink} />
                </span>
              </button>
              <span className="text-sm italic mt-2" style={{ color: PALETTE.ink + "90" }}>
                Er hoeft nog niets vast te staan.
              </span>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 aspect-square">
              <div className="col-span-4 row-span-4 rounded-3xl overflow-hidden">
                <img src={IMGS.skate} alt="Skateboarders" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden" style={{ background: PALETTE.lichtblauw }}>
                <img src={IMGS.klimmen} alt="Klimmen" className="w-full h-full object-cover mix-blend-multiply opacity-90" />
              </div>
              <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden">
                <img src={IMGS.lachen} alt="Lachen" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-4 row-span-2 rounded-3xl overflow-hidden">
                <img src={IMGS.jumpen} alt="Springen" className="w-full h-full object-cover" />
              </div>
            </div>
            {/* Handwritten note */}
            <motion.div
              initial={{ rotate: 5, opacity: 0, scale: 0.9 }}
              animate={{ rotate: 5, opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.6, ease }}
              className="absolute -bottom-6 -left-4 md:left-8 px-5 py-4 rounded-3xl shadow-2xl max-w-[220px]"
              style={{ background: PALETTE.geel, color: PALETTE.ink }}
            >
              <p className="font-heading italic text-lg leading-tight">"Ik had gewoon iemand nodig die vroeg wat ik lééuk vond."</p>
              <p className="mt-2 text-xs uppercase tracking-widest opacity-70">— Deelnemer</p>
            </motion.div>
          </div>
        </div>

        <div className="mt-24 flex justify-center">
          <Squiggle color={PALETTE.lichtblauw} className="w-full max-w-3xl h-16" />
        </div>
      </div>
    </section>
  );
}

function HeroOpdrachtgever() {
  return (
    <section className="relative overflow-hidden" style={{ background: PALETTE.surface, color: PALETTE.ink }}>
      <div className="absolute top-20 left-1/2 w-20 h-20 rounded-full hidden md:block" style={{ background: PALETTE.koraal }} aria-hidden />
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-24 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.35em]" style={{ color: PALETTE.koraal }}>
              Voor gemeenten & scholen
            </p>
            <h2 className="font-heading font-light text-4xl sm:text-5xl lg:text-7xl leading-[0.98] tracking-tight mt-6">
              Geef mensen
              <span className="italic relative inline-block ml-3">
                speelruimte
                <Squiggle color={PALETTE.koraal} className="absolute left-0 -bottom-2 w-full h-3" delay={0.3} />
              </span>
              <br />
              en zie wat er ontstaat.
            </h2>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed" style={{ color: PALETTE.ink + "c0" }}>
              Meetbare beweging waar niets bewoog. Twaalf maanden ontwikkelfase — vóór regulier traject, met nul-, tussen- en eindmetingen.
            </p>

            <div className="mt-12 flex flex-wrap gap-4">
              <div className="rounded-3xl p-6 min-w-[180px]" style={{ background: PALETTE.geel }}>
                <p className="text-xs uppercase tracking-[0.25em]">Cohort · 12</p>
                <p className="font-heading text-5xl mt-2">+34%</p>
                <p className="text-sm mt-1">belastbaarheid</p>
              </div>
              <div className="rounded-3xl p-6 min-w-[180px]" style={{ background: PALETTE.lichtblauw }}>
                <p className="text-xs uppercase tracking-[0.25em]">Overdracht</p>
                <p className="font-heading text-5xl mt-2">8/12</p>
                <p className="text-sm mt-1">warm doorgezet</p>
              </div>
              <div className="rounded-3xl p-6 min-w-[180px]" style={{ background: PALETTE.koraal, color: PALETTE.bg }}>
                <p className="text-xs uppercase tracking-[0.25em]">Kosten</p>
                <p className="font-heading text-5xl mt-2">€3–10K</p>
                <p className="text-sm mt-1">per deelnemer</p>
              </div>
            </div>

            <div className="mt-12">
              <button className="group inline-flex items-center gap-4 rounded-full pl-7 pr-2 py-2 transition-all" style={{ background: PALETTE.ink, color: PALETTE.bg }}>
                <span className="text-base font-medium">Verken de pilot</span>
                <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: PALETTE.koraal }}>
                  <ArrowUpRight size={18} color="white" />
                </span>
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-4">
            <div className="rounded-3xl overflow-hidden aspect-[4/5]">
              <img src={IMGS.spelen} alt="Samen ontdekken" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-3xl p-6" style={{ background: PALETTE.geel + "50" }}>
              <p className="font-heading italic text-2xl leading-tight">
                "Voor het eerst in vier jaar zag ik iemand weer een keuze maken."
              </p>
              <p className="mt-3 text-xs uppercase tracking-widest opacity-70">— Klantmanager, Rotterdam</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function MoodboardC() {
  const [tab, setTab] = useState("deelnemer");
  usePageMeta({ title: "Route C · Speelruimte · Moodboard" });

  return (
    <div data-testid="moodboard-c" style={{ background: PALETTE.bg }}>
      <Toolbar current="c" tab={tab} setTab={setTab} palette={PALETTE} />
      {tab === "deelnemer" ? <HeroDeelnemer /> : <HeroOpdrachtgever />}
      <PaletteFooter palette={PALETTE} name="Speelruimte" font='"Cormorant Garamond" + "Manrope" + hand-drawn accents' />
    </div>
  );
}
